import { Model } from '@vuex-orm/core';

// eslint-disable-next-line import/order
import { Branding } from '@/models';

import { redirectToLogin, getHomeRoute, hasTierAccess, getCurrentOrganisation, getCurrentAccount } from '@core/auth';
import ErrorService from '@core/services/ErrorService';
import store from '@core/store';

import GrowthInvestRoleEnum from '@constants/enums/growthInvest/role';
import InvestorStructureEnum from '@constants/enums/investor/structure';
import UserRoleEnum from '@constants/enums/user/role';

import SharedApi from '@packages/shared/api';
import RegistrationApi from '@packages/registrations/api';
import BrandingApi from '@packages/branding/api';

import { toDate } from '@utils/date';

import clock from '@plugins/dayjs';

export class AuthModel extends Model {
    static entity = 'auth';

    static fields() {
        return {
            id: this.string(null),
            _user: this.attr(null),
            _organisation: this.attr(null),
            _account: this.attr(null),
            _investor: this.attr(null),
            _adviser: this.attr(null),
            _onboarding: this.boolean(false),
            _registration: this.attr(null),
            _permissions: this.attr([]),
            branding_id: this.string(null).nullable()
        };
    }

    set user(payload) {
        this._user = payload ? payload : null;

        if (payload && 'id' in payload) {
            this.id = payload.id;
        } else {
            this.id = null;
        }

        this.$save();
    }

    get user() {
        return this._user;
    }

    set organisation(payload) {
        this._organisation = payload ? payload : null;
        this._permissions = payload && payload.permissions ? [...payload.permissions] : [];
        this.$save();

        this.setUnreadNotifications();
    }

    get organisation() {
        return this._organisation;
    }

    set account(payload) {
        this._account = payload ? payload : null;
        this.$save();

        this.setUnreadNotifications();
    }

    get account() {
        return this._account;
    }

    set investor(payload) {
        this._investor = payload ? payload : null;
        this.$save();
    }

    get investor() {
        return this._investor;
    }

    set adviser(payload) {
        this._adviser = payload ? payload : null;
        this.$save();
    }

    get adviser() {
        return this._adviser;
    }

    set onboarding(payload) {
        this._onboarding = payload ? payload : false;
        this.$save();
    }

    get onboarding() {
        return this._onboarding;
    }

    set registration(payload) {
        this._registration = payload ? payload : null;

        if (payload && 'id' in payload) {
            this.id = payload.id;
        } else {
            this.id = null;
        }

        this.$save();
    }

    get registration() {
        return this._registration;
    }

    branding(id = null) {
        return Branding.find(id || this.branding_id) || Branding.find('public') || new Branding();
    }

    // Getters

    get user_id() {
        if (!this.has_user) {
            return null;
        }

        return this.user.id;
    }

    get user_name() {
        if (this.user && 'name' in this.user) {
            return this.user.name;
        }
        if (this.registration && 'name' in this.registration) {
            return this.registration.name;
        }
        return null;
    }

    get user_first_name() {
        if (!this.user_name) {
            return null;
        }

        return this.user_name.split(' ')[0];
    }

    get user_email() {
        if (!this.has_user) {
            return null;
        }

        return this.user.email;
    }

    get adviser_id() {
        if (this.is_investor) {
            if (this.has_account) {
                return this.account.adviser_id;
            } else if (this.accounts.length) {
                return this.accounts[0].adviser_id;
            } else {
                return null;
            }
        } else if (this.is_adviser) {
            return this.organisation_id;
        }

        return null;
    }

    get linked_users() {
        if (!this.has_user) {
            return [];
        }
        return this.user.linked_users || [];
    }

    get organisations() {
        if (!this.has_user) {
            return [];
        }

        let orgs = [...this.user.organisations];

        for (const link of this.linked_users) {
            orgs.push(...link.organisations);
        }

        orgs = orgs.filter((org, index, self) => self.indexOf(org) === index);

        orgs.sort((a, b) => a.name.localeCompare(b.name));

        return orgs;
    }

    get organisation_id() {
        if (!this.organisation) {
            return null;
        }
        return this.organisation.id;
    }

    get organisation_type() {
        if (!this.organisation) {
            return null;
        }

        return this.organisation.type;
    }

    get organisation_roles() {
        if (!this.organisation) {
            return null;
        }

        return this.organisation.roles;
    }

    get account_id() {
        if (!this.has_account) {
            return null;
        }

        return this.account.adviser_id;
    }

    get accounts() {
        if (!this.organisation) {
            return [];
        }

        return this.organisation.accounts || [];
    }

    get category_expires_on() {
        if (!this.organisation) {
            return null;
        }

        return this.organisation.category_expires_on;
    }

    get category_expired() {
        if (!this.category_expires_on) {
            ErrorService.logSentryEvent('Category expires on is falsy', this.category_expires_on);
            return true;
        }

        const date = toDate(this.category_expires_on, false);

        if (!date) {
            ErrorService.logSentryEvent('Category expires on is not a valid date', this.category_expires_on);
            return true;
        }

        return date.isBefore(clock());
    }

    get hostname() {
        if (this.is_investor && this.has_account && this.account.base_url) {
            return this.account.base_url;
        } else if (this.has_organisation && this.organisation.base_url) {
            return this.organisation.base_url;
        }
        return window.location.hostname;
    }

    get scope_organisation_name() {
        let name = null;

        if (this.has_account) {
            name = this.account.adviser_name;
        } else if (this.accounts && this.accounts.length) {
            name = this.accounts[0].adviser_name;
        }

        return name;
    }

    get scope_organisation_id() {
        let id = null;

        if (this.has_account) {
            id = this.account.adviser_id;
        } else if (this.accounts && this.accounts.length) {
            id = this.accounts[0].adviser_id;
        }

        return id;
    }

    get headers() {
        const headers = {};

        if (!this.is_logged_in) {
            return headers;
        }

        if (this.is_investor) {
            headers['X-GI-Role'] = this.organisation_id;
            headers['X-GI-Scope'] = this.scope_organisation_id;
        } else {
            headers['X-GI-Role'] = this.organisation_id;
            headers['X-GI-Scope'] = this.organisation_id;
        }

        return headers;
    }

    get permissions() {
        if (!this.organisation) {
            return [];
        }

        return this._permissions;
    }

    togglePermission(permission) {
        if (this.has_permission(permission)) {
            this.permissions.splice(this.permissions.indexOf(permission), 1);
        } else {
            this.permissions.push(permission);
        }
        this.$save();
    }

    resetPermissions() {
        this._permissions = this.organisation.permissions ? [...this.organisation.permissions] : [];
        this.$save();
    }

    get roles() {
        if (!this.organisation) {
            return [];
        }
        return this.organisation.roles;
    }

    get signout_url() {
        if (!this.has_user) {
            return null;
        }
        return this.user.signout_url;
    }

    get home_route() {
        return getHomeRoute(this.organisation);
    }

    // Helpers

    get is_investor() {
        return this.organisation_type === 'INVESTOR';
    }

    get is_individual_investor() {
        if (!this.is_investor || !this.investor) {
            return false;
        }

        return this.investor.investor_structure === InvestorStructureEnum.INDIVIDUAL;
    }

    // Execution Only
    get is_xo_investor() {
        if (!this.is_investor || !this.investor) {
            return false;
        }

        return this.investor.adviser_accounts.some(aa => {
            return aa.advised === false && aa.adviser?.id === this.account_id;
        });
    }

    get is_advised_investor() {
        if (!this.is_investor || !this.investor) {
            return false;
        }

        return this.investor.adviser_accounts.some(aa => {
            return aa.advised === true && aa.adviser?.id === this.account_id;
        });
    }

    get is_investor_authorised() {
        if (!this.is_investor) {
            return false;
        }
        if (!this.investor) {
            return false;
        }

        return this.investor.investment_account_authorised_at !== null;
    }

    get is_adviser() {
        return this.organisation_type === 'ADVISER';
    }

    get is_fund_manager() {
        return this.organisation_type === 'FUND_MANAGER';
    }

    get is_provider() {
        return this.organisation_type === 'PRODUCT_PROVIDER';
    }

    get is_company() {
        return this.organisation_type === 'COMPANY';
    }

    get is_gi() {
        return Object.keys(GrowthInvestRoleEnum).some(role => this.has_role(role));
    }

    get has_gi_superadmin() {
        return this.organisations.some(organisation => {
            return organisation.roles.includes(GrowthInvestRoleEnum.PLATFORM_SUPERADMIN);
        });
    }

    get has_role_in_gi() {
        return this.organisations.some(organisation => {
            return Object.keys(GrowthInvestRoleEnum).some(role => organisation.roles.includes(role));
        });
    }

    get is_initialised() {
        if (!this.organisation) {
            return false;
        }
        if (this.is_investor && this.accounts.length && !this.has_account) {
            return false;
        }
        return true;
    }

    get is_logged_in() {
        return this.has_user;
    }

    get has_user() {
        return !!this.user;
    }

    get has_organisation() {
        return !!this.organisation;
    }

    get has_account() {
        return !!this.account;
    }

    get has_branding() {
        return !!(this.branding_id && Branding.find(this.branding_id));
    }

    get has_accounts() {
        return this.accounts.length > 0;
    }

    get has_valid_hostname() {
        return this.hostname && this.hostname === window.location.hostname;
    }

    get has_role() {
        return role => this.roles.includes(role);
    }

    get has_permission() {
        return permission => this.permissions.includes(permission);
    }

    get can() {
        return this.has_permission;
    }

    get is_owner() {
        return this.has_role(UserRoleEnum.OWNER);
    }

    get is_superuser() {
        if (this.has_role(UserRoleEnum.SUPERUSER)) {
            return true;
        }

        return this.is_gi;
    }

    get is_accounts() {
        return this.has_role(UserRoleEnum.ACCOUNTS);
    }

    get disable_cobranded_reports() {
        return this.adviser && this.adviser.disable_cobranded_reports;
    }

    // Setup

    async ping() {
        const user = await new SharedApi().methods.getCurrentUser();
        const org = getCurrentOrganisation(user);
        const acc = getCurrentAccount(org);

        const unreadCount = this.getUnreadNotificationsCount(org, acc);

        if (unreadCount !== store.getters['app/unread_notifications_count']) {
            this.setUnreadNotificationsCount(unreadCount);
        }

        return user;
    }

    getUnreadNotificationsCount(org = null, acc = null) {
        if (!store.getters['features/enabled']('notifications')) {
            return 0;
        }

        org = org || this.organisation;
        acc = acc || this.account;

        let unreadCount = 0;

        if (acc) {
            unreadCount = acc.notifications ? acc.notifications.unread : 0;
        } else if (org) {
            unreadCount = org.notifications ? org.notifications.unread : 0;
        }

        return unreadCount;
    }

    setUnreadNotificationsCount(count = 0) {
        store.dispatch('app/setUnreadNotificationsCount', count);
    }

    setUnreadNotifications(org = null, acc = null) {
        const unreadCount = this.getUnreadNotificationsCount(org, acc);
        this.setUnreadNotificationsCount(unreadCount);
    }

    async login(newTab = false) {
        return redirectToLogin(
            {
                rd: window.location.origin,
                app_uri: window.location.origin
            },
            newTab
        );
    }

    async setCurrentUser() {
        const user = await new SharedApi().methods.getCurrentUser();

        if (!this.user || (user && user.id !== this.user.id)) {
            this.user = user;
        }

        this.onboarding = false;

        const org = getCurrentOrganisation(user);

        if ((!this.organisation && org) || (org && org.id !== this.organisation.id)) {
            this.organisation = org;
        }

        const acc = getCurrentAccount(this.organisation);

        if ((!this.account && acc) || (acc && acc.adviser_id !== this.account.adviser_id)) {
            this.account = acc;
        }

        this.setUnreadNotifications(org, acc);

        return this;
    }

    async setRegistration() {
        try {
            let registration = (await new RegistrationApi().methods.get()).data[0];
            this.registration = await this.setRegistrationTemplate(registration);
            await this.$save();
            this.onboarding = true;
        } catch (error) {
            if (error?.code === 404) {
                await new RegistrationApi().methods.post();
                await this.setRegistration();
            } else {
                throw error;
            }
        }
    }

    async setRegistrationTemplate(registration) {
        const template = {
            declarations: [],
            questions: [],
            terms: []
        };

        const [d, q, t] = await Promise.allSettled([
            new RegistrationApi().methods.getDeclarations(),
            new RegistrationApi().methods.getQuestions(),
            new RegistrationApi().methods.getTerms()
        ]);

        template.declarations = d.status === 'fulfilled' ? d.value.data : [];
        template.questions = q.status === 'fulfilled' ? q.value.data : [];
        template.terms = t.status === 'fulfilled' ? t.value.data : [];

        registration.template = template;

        return registration;
    }

    setUser(user, organisation_id = null, account_id = null) {
        this.user = user;

        if (!this.user) {
            this.organisation = null;
            this.account = null;
            this.onboarding = false;
            this.investor = null;
            this.adviser = null;
        } else {
            if (organisation_id) {
                this.organisation = user.organisations.find(org => org.id === organisation_id);
            } else {
                this.organisation = user.organisations[0];
            }

            if (this.organisation.type === 'INVESTOR') {
                if (account_id) {
                    this.account = this.organisation.accounts.find(org => org.id === account_id);
                } else {
                    this.account = this.organisation.accounts[0];
                }
            } else {
                this.account = null;
            }

            this.onboarding = false;
        }
    }

    getHostOrganisations() {
        const hostname = window.location.hostname;

        if (!this.has_user) {
            return [];
        }

        return this.user.organisations.filter(org => {
            if (org.type === 'INVESTOR') {
                return org.accounts.find(acc => acc.base_url === hostname);
            } else {
                return org.base_url === hostname;
            }
        });
    }

    getHostAccounts() {
        const hostname = window.location.hostname;

        if (!this.organisation) {
            return [];
        }

        return this.organisation.accounts.filter(acc => acc.base_url === hostname);
    }

    async setBranding() {
        let data = [];

        if (!Branding.find('public')) {
            const publicBranding = await new SharedApi().methods.getPublicBranding();

            data.push({
                id: 'public',
                name: 'Public',
                _text: publicBranding.text,
                _colours: publicBranding.colours,
                _images: publicBranding.images,
                _dark: publicBranding.dark,
                _custom_app_url: publicBranding.custom_app_url
            });
        }

        try {
            data = [...data, ...(await new BrandingApi().methods.get())];
        } catch (error) {
            console.error('Error fetching branding:', error);
        }

        Branding.insert({ data });

        const customBrandingId = this.is_investor ? this.adviser_id : this.organisation_id;
        let hasCustomBranding = !!data.find(branding => branding.id === customBrandingId);

        if ((this.is_fund_manager || this.is_provider) && !hasTierAccess(this.organisation, 1)) {
            hasCustomBranding = false;
        }

        this.branding_id = hasCustomBranding ? customBrandingId : 'public';
        this.$save();
    }
}

export default AuthModel;
